import { Observable, of, Subject } from 'rxjs'
import { mergeAll, shareReplay, tap } from 'rxjs/operators'
import { ObservableCacheMethod } from './common'

export interface ICacheStorageItem {
  observable: Observable<any>
  arguments: string
  created: Date
}

export interface ICacheOptions {
  /**
   * store for data | currently I am housing the observables and serving it up
   */
  storage?: ICacheStorage

  /**
   * force refresh will get new data
   */
  forceRefresh$?: Observable<unknown> | Subject<unknown>

  /**
   * force removal of all observables
   */
  forceRemove$?: Observable<unknown> | Subject<unknown>

  /**
   * time after last request that we will automatically get new data
   */
  autoForceAfter?: number
}

interface ICacheStorage {
  setItem(key: string, item: ICacheStorageItem): void
  getItem(key: string): ICacheStorageItem | undefined
  removeItem(key: string): void
  removeItemByParamaters(paramaters: any[]): void
  buildKey(constructorName: string, methodName: string, args: any[]): string
  createItem(args: any[], observerable: Observable<any>): ICacheStorageItem
}

export class CacheStorage implements ICacheStorage {
  private readonly cache = new Map<string, ICacheStorageItem>()

  /** create item */
  createItem(args: any[], observerable: Observable<any>): ICacheStorageItem {
    return {
      arguments: this.formatArgs(args),
      created: new Date(),
      observable: observerable,
    }
  }

  /** build key for map */
  buildKey(constructorName: string, methodName: string, args: any[]): string {
    return `${constructorName}_${methodName}_${this.formatArgs(args)}`
  }

  /** set item in cache */
  setItem(key: string, item: ICacheStorageItem): void {
    this.cache.set(key, item)
  }

  /** get item from cache by key */
  getItem(key: string): ICacheStorageItem | undefined {
    return this.cache.get(key)
  }

  /** remove item by key */
  removeItem(key: string): void {
    this.cache.delete(key)
  }

  /** remove item by paramaters : this is build off of a non shared store */
  removeItemByParamaters(paramaters: any[]) {
    const itemKey = this.getKeyByParamaters(paramaters)
    this.cache.delete(itemKey)
  }

  private formatArgs(args: any[]) {
    return JSON.stringify(args)
  }

  private getKeyByParamaters(args: any[]): string {
    const formattedArgs = this.formatArgs(args)

    const items = [...this.cache.entries()]
      .filter(({ 1: v }) => v.arguments === formattedArgs)
      .map(([k]) => k)

    if (items.length > 0) {
      return items[0]
    } else {
      return null
    }
  }
}

export function ObservableCache<T extends Record<string, any>>(
  config: ICacheOptions = {}
) {
  return (
    target: T,
    methodName: string,
    descriptor: TypedPropertyDescriptor<ObservableCacheMethod<Observable<any>>>
  ): TypedPropertyDescriptor<ObservableCacheMethod<Observable<any>>> => {
    // setup the cache if we do not already have one
    // this is going to be one storage per decorator. None shared
    const storage = !config.storage ? new CacheStorage() : config.storage
    const originalMethod = descriptor.value

    function isExpired(config: ICacheOptions, cacheItem: ICacheStorageItem) {
      return (
        config.autoForceAfter &&
        new Date().getTime() - new Date(cacheItem.created).getTime() >
          config.autoForceAfter
      )
    }

    descriptor.value = function (...args: any[]): Observable<any> {
      const key = storage.buildKey(target.constructor.name, methodName, args)
      const cacheItem = storage.getItem(key)

      if (cacheItem) {
        if (isExpired(config, cacheItem)) {
          storage.removeItem(key)
        } else {
          return cacheItem.observable
        }
      }

      // delete subscription from storage and this will allow us to make another request
      if (config.forceRemove$) {
        config.forceRemove$.subscribe((_) => {
          storage.removeItem(key)
        })
      }

      // delete from storage when we want to refresh. This can be for a set of paramaters, or this
      // will be for the key from the cache destroy decorator
      if (config.forceRefresh$) {
        config.forceRefresh$.subscribe((value: any[]) => {
          if (value) {
            storage.removeItemByParamaters(value)
          } else {
            storage.removeItem(key)
          }
        })
      }

      // build a new observable off of the original.
      // clear observeables that have cached outside
      const observable = of(
        originalMethod.apply(this, args)
        // refreshSubject.pipe(switchMap(() => originalMethod.apply(this, args)))
      ).pipe(
        mergeAll(),
        shareReplay(1),
        tap(() => storage.removeItem(key))
      )

      const newStorageItem = storage.createItem(args, observable)
      storage.setItem(key, newStorageItem)

      return observable
    }

    return descriptor
  }
}
